import { useEffect, useState } from 'react';
import {
  DurationUnit,
  ProductPrice,
  RecurringType,
  RentalProductPrice,
  SaleProductPrice,
} from '../../clients/quipli-service/pricing/pricing';
import { useAPI } from '../../clients/quipli-service/quipli-service-client';
import { DataTable } from '../../components/table/data-table';
import { RentalPriceModal } from './rental-price-modal/rental-price-modal';
import { Info } from 'luxon';
import { addNumericSuffix } from '../../util/util';
import { PricingPreview } from './pricing-preview';
import { Dictionary, groupBy, keyBy, partition, toArray } from 'lodash';
import { SalePriceModal } from './sale-price-modal/sale-price-modal';
import { toast } from '../../util/toasts';
import { ApiError } from '../../clients/axios-instance';
import { OrderType } from '../../clients/quipli-service/order/order';
import { Button } from '../../components/form/button';
import Icon from '../../components/icons/icon';

type RentalProductPriceTableRowData = {
  id: string;
  name: string | undefined;
  duration: string;
  price: string;
  from: string | undefined;
  to: string | undefined;
  recurring: string;
  locations: string;
  featured: JSX.Element;
  actions: string | JSX.Element;
};

type SaleProductPriceTableRowData = Omit<RentalProductPriceTableRowData, 'duration' | 'recurring' | 'featured'>;

export const PricingTab = ({
  productId,
  forRent,
  forSale,
}: {
  productId: number;
  forRent: boolean;
  forSale: boolean;
}) => {
  const { deletePrice, listProductPrices, listLocations, getProduct, getConfiguration } = useAPI();
  const [prices, setPrices] = useState<ProductPrice[]>([]);
  const [rentalPricesData, setRentalPricesData] = useState<RentalProductPriceTableRowData[]>([]);
  const [salePricesData, setSalePricesData] = useState<SaleProductPriceTableRowData[]>([]);
  const [locations, setLocations] = useState<{ value: number; label: string }[]>([]);
  const [locationsKeyed, setLocationsKeyed] = useState<Dictionary<{ value: number; label: string }>>();
  const [selectedPrice, setSelectedPrice] = useState<ProductPrice | undefined>();
  const [isPricingModalOpen, setIsPricingModalOpen] = useState<OrderType | undefined>();
  const [showPricingPreview, setShowPricingPreview] = useState<boolean>(false);
  const [showRangePriceUnits, setShowRangePriceUnits] = useState<boolean>(false);

  const currencyFormat = Intl.NumberFormat('us-en', { currency: 'USD', minimumFractionDigits: 2 });
  const translateDaysOfWeek = (dow: number[]): string =>
    dow.length === 7 ? 'week' : dow.map((day) => Info.weekdays('short')[day - 1]).join(', ');
  const translateDaysOfMonth = (daysOfMonth: number[]): string => {
    return daysOfMonth.map((day) => (day === -1 ? 'last' : addNumericSuffix(day))).join(', ');
  };
  const translateWeeksOfMonth = (wom: number[]): string =>
    wom.map((week) => (week === -1 ? 'last' : addNumericSuffix(week))).join(', ');

  const rentalColumnNames = [
    { key: 'name', label: 'Name' },
    { key: 'duration', label: 'Price Per' },
    { key: 'price', label: 'Rental Price, $' },
    { key: 'recurring', label: 'Repeats Every' },
    { key: 'from', label: 'Starts On' },
    { key: 'to', label: 'Expires On' },
    { key: 'featured', label: 'Show Price' },
    { key: 'locations', label: 'Locations' },
    { key: 'actions', label: ' ' },
  ];

  const saleColumnNames = [
    { key: 'name', label: 'Name' },
    { key: 'price', label: 'Sale Price, $' },
    { key: 'from', label: 'Starts On' },
    { key: 'to', label: 'Expires On' },
    { key: 'locations', label: 'Locations' },
    { key: 'actions', label: ' ' },
  ];

  const reloadPrices = async () => {
    const thePrices = await listProductPrices(productId);
    setPrices(thePrices);
    const [rentalPrices, salePrices] = partition(
      thePrices.filter(
        (p) =>
          p.type === OrderType.Sale ||
          (p.type === OrderType.Rental && p.durationUnit !== DurationUnit.minute && p.durationValue !== 1440),
      ),
      (p) => p.type === OrderType.Rental,
    );
    const rpps = showRangePriceUnits
      ? toArray(
          groupBy(
            (rentalPrices as RentalProductPrice[]).sort((a, b) => a.durationValue - b.durationValue),
            (p) => p.durationUnit,
          ),
        ).flat()
      : (rentalPrices as RentalProductPrice[]);
    const rpd = rpps
      .sort((a, b) => a.durationMinutes - b.durationMinutes)
      .map((rpp) => {
        const specialPricingDescription =
          rpp.recurringType === RecurringType.Weekly
            ? `Every ${translateDaysOfWeek(rpp.recurringDaysOfWeek!)}`
            : rpp.recurringType === RecurringType.Biweekly
            ? `Every other ${translateDaysOfWeek(rpp.recurringDaysOfWeek!)}`
            : rpp.recurringType === RecurringType.DateMonthly
            ? `Every ${translateDaysOfMonth(rpp.recurringDaysOfMonth!)} of the month`
            : rpp.recurringType === RecurringType.DayOfWeekMonthly
            ? `Every ${translateWeeksOfMonth(rpp.recurringWeeksOfMonth!)} ${translateDaysOfWeek(
                rpp.recurringDaysOfWeek!,
              )} of the month`
            : 'Daily';
        const locationsStr =
          rpp.locationIds && rpp.locationIds.length > 0
            ? rpp.locationIds.reduce((str: string, locationId: number) => {
                return `${str}${locationsKeyed ? locationsKeyed[locationId]?.label : ''}, `;
              }, '')
            : 'All Locations, ';
        return {
          id: rpp.id,
          name: rpp.name,
          duration: rpp.durationLabel || '',
          price: currencyFormat.format(rpp.price / 100),
          from: rpp.from
            ? rpp.from.toFormat('LLLL d, y')
            : rpp.createdAt.toMillis() != 0
            ? rpp.createdAt.toFormat('LLLL d, y')
            : undefined,
          to: rpp.to && rpp.to.toFormat('LLLL d, y'),
          recurring: specialPricingDescription,
          locations: locationsStr.slice(0, locationsStr.length - 2),
          featured: (
            <span className="flex ml-8">
              <i className={`fa-regular ${rpp.featured ? 'fa-square-check' : 'fa-square'}`} />
            </span>
          ),
          actions: rpp.deletedAt ? (
            ''
          ) : (
            <div className="flex justify-end">
              <i
                className="fa-solid fa-pencil mr-3 cursor-pointer"
                onClick={() => {
                  setSelectedPrice(rpp);
                  setIsPricingModalOpen(OrderType.Rental);
                }}
              />
              <i className="fa-solid fa-trash-can cursor-pointer" onClick={() => deactivatePrice(rpp.id)} />
            </div>
          ),
        };
      });
    setRentalPricesData(rpd);
    const spd = (salePrices as SaleProductPrice[]).map((spp) => {
      const locationsStr =
        spp.locationIds && spp.locationIds.length > 0
          ? spp.locationIds.reduce((str: string, locationId: number) => {
              return `${str}${locationsKeyed ? locationsKeyed[locationId]?.label : ''}, `;
            }, '')
          : 'All Locations, ';
      return {
        id: spp.id,
        name: spp.name,
        price: currencyFormat.format(spp.price / 100),
        from: spp.from
          ? spp.from.toFormat('LLLL d, y')
          : spp.createdAt.toMillis() != 0
          ? spp.createdAt.toFormat('LLLL d, y')
          : undefined,
        to: spp.to && spp.to.toFormat('LLLL d, y'),
        locations: locationsStr.slice(0, locationsStr.length - 2),
        actions: spp.deletedAt ? (
          ''
        ) : (
          <div className="flex justify-end">
            <i
              className="fa-solid fa-pencil mr-3 cursor-pointer"
              onClick={() => {
                setSelectedPrice(spp);
                setIsPricingModalOpen(OrderType.Sale);
              }}
            />
            <i className="fa-solid fa-trash-can cursor-pointer" onClick={() => deactivatePrice(spp.id)} />
          </div>
        ),
      };
    });
    setSalePricesData(spd);
  };

  useEffect(() => {
    getProduct(productId).then(async (product) => {
      const res = await listLocations();
      const filtered: { value: number; label: string }[] = [];
      res.forEach((l) => {
        if (product.locationIds.includes(l.id)) filtered.push({ value: l.id, label: l.name });
      });
      setLocations(filtered);
      setLocationsKeyed(keyBy(filtered, (o) => o.value));
    });
  }, [productId]);

  useEffect(() => {
    if (locationsKeyed) {
      reloadPrices();
    }
  }, [locationsKeyed]);

  useEffect(() => {
    getConfiguration('CONF_SHOW_RANGE_PRICE_UNITS').then((showRangePriceUnits) =>
      setShowRangePriceUnits(showRangePriceUnits?.value === '1'),
    );
  }, []);

  const deactivatePrice = async (priceId: string) => {
    // TODO: maybe confirm delete modal
    await deletePrice(priceId)
      .then(() => {
        toast('Successfully deleted price', 'success');
      })
      .catch((err: ApiError) => {
        const errMsg = err.status === 400 && err.message ? err.message : 'Error deleting price';
        toast(errMsg, 'error');
      });
    return reloadPrices();
  };

  return (
    <div className="pricing-tab m-4 font-sans space-y-6">
      <div className="mb-3 pb-3 flex items-center border-b border-b-gray-300 border-solid">
        <span className="mx-4 p-0 text-2xl font-light capitalize text-gray-700">Pricing</span>
        <Button
          className="button-primary mr-4 cursor-pointer rounded border border-solid border-gray-300 bg-white px-4 py-2 text-sm text-gray-500 shadow-sm hover:bg-gray-200"
          onClick={() => setShowPricingPreview(!showPricingPreview)}
        >
          {showPricingPreview ? 'Hide Preview' : 'Preview'}
        </Button>
      </div>

      {showPricingPreview && (
        <div className="my-6">
          <PricingPreview
            productId={productId}
            locations={locations}
            prices={prices}
            forRent={forRent}
            forSale={forSale}
            showRangePriceUnits={showRangePriceUnits}
          />
        </div>
      )}

      {forRent ? (
        <div className="rental-section">
          <div className="mb-3 flex items-end items-center justify-between">
            <span className="ml-4 p-0 text-xl font-light capitalize text-gray-700">Rental Prices</span>
            <Button
              className="button-primary mr-4 cursor-pointer rounded border border-solid border-gray-300 bg-white px-4 py-2 text-sm text-gray-500 shadow-sm hover:bg-gray-200"
              onClick={() => {
                setSelectedPrice(undefined);
                setIsPricingModalOpen(OrderType.Rental);
              }}
            >
              <span className="flex items-center">
                <Icon icon="Plus" className="pr-2" stroke="white" />
                Add Rental Price
              </span>
            </Button>
          </div>
          <DataTable
            columnNames={rentalColumnNames}
            className="pricing-table"
            data={rentalPricesData.map((d) => ({ mobileHeader: null, fields: d }))}
            showAlwaysDesktop={true}
            noDataText="No rental pricing yet"
          />

          <RentalPriceModal
            isOpen={isPricingModalOpen === OrderType.Rental}
            productId={productId}
            price={selectedPrice as RentalProductPrice}
            close={async (refresh: boolean = false) => {
              setSelectedPrice(undefined);
              await (refresh ? reloadPrices() : Promise.resolve());
              setIsPricingModalOpen(undefined);
            }}
            locationOptions={locations}
          />
        </div>
      ) : (
        <></>
      )}

      {forSale ? (
        <div className="sale-section">
          <div className="mb-3 flex items-end items-center justify-between">
            <span className="ml-4 p-0 text-xl font-light capitalize text-gray-700">Sale Prices</span>
            <Button
              className="button-primary"
              onClick={() => {
                setSelectedPrice(undefined);
                setIsPricingModalOpen(OrderType.Sale);
              }}
            >
              <span className="flex items-center">
                <Icon icon="Plus" className="pr-2" stroke="white" />
                Add Sale Price
              </span>
            </Button>
          </div>
          <DataTable
            columnNames={saleColumnNames}
            data={salePricesData.map((d) => ({ mobileHeader: null, fields: d }))}
            showAlwaysDesktop={true}
            noDataText="No sale pricing yet"
          />

          <SalePriceModal
            isOpen={isPricingModalOpen === OrderType.Sale}
            productId={productId}
            price={selectedPrice as SaleProductPrice}
            close={async (refresh: boolean = false) => {
              setSelectedPrice(undefined);
              await (refresh ? reloadPrices() : Promise.resolve());
              setIsPricingModalOpen(undefined);
            }}
            locationOptions={locations}
          />
        </div>
      ) : (
        <></>
      )}
    </div>
  );
};
